import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Typography, styled } from '@mui/material'
import { shallow } from 'zustand/shallow'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import toast from 'react-hot-toast'
import { Avatar, Button, FormGroup, Input, SearchInput } from '@/design-system'
import { fireAlertToast } from '@/design-system/alerts'
import useAppStore from '@/store/useAppStore'
import { useLocalStorage } from '@/hooks/common'
import { validateEmail } from '@/utils/helper'
import { ProfilePrivacyData, GroupOptions } from '@/utils/constants'
import {
  useGetPermissionsProfile,
  useUpdateVisibility,
  useUpdateProfile,
  useGetBasicInfo,
  useGetMyCircles,
  useCreateGroup,
  useInviteUser,
} from '@/hooks/api/dashboard'
import { Toggle } from '../Toggle'
import { avatarGradient, circleTypeStyle } from '@/features/connections/utils'
import { isValidAvatarUrl } from '@/utils/helper'

// ─── Shared styled atoms (matches settings pattern) ──────────────────────────

const PanelHead = styled(Box)({ marginBottom: 18 })
const H2 = styled(Typography)({ fontSize: 20, fontWeight: 720, letterSpacing: '-0.025em', color: 'var(--ds-ink)' })
const Sub = styled(Typography)({ fontSize: 13, color: 'var(--ds-ink-subtle)', marginTop: 4 })

const SetRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '16px 0',
  borderTop: '1px solid var(--ds-border)',
  '&:first-of-type': { borderTop: 'none', paddingTop: 0 },
})

const RowLft = styled(Box)({ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 })
const RowIc = styled(Box)({ width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 })
const RowT = styled('div')({ fontSize: 13.5, fontWeight: 640, color: 'var(--ds-ink)' })
const RowS = styled('div')({ fontSize: 12, color: 'var(--ds-ink-subtle)', marginTop: 2 })

// ─── Visibility option cards ──────────────────────────────────────────────────

const VisibilityGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 12,
  marginTop: 4,
  '@media (max-width: 640px)': { gridTemplateColumns: '1fr' },
})

const VisCard = styled(Box, {
  shouldForwardProp: (p) => p !== '$selected',
})<{ $selected?: boolean }>(({ $selected }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: '14px 16px',
  borderRadius: 'var(--ds-radius-md)',
  border: `1.5px solid ${$selected ? 'var(--ds-accent-deep-b, #8b5cf6)' : 'var(--ds-border)'}`,
  background: $selected ? 'color-mix(in srgb, var(--ds-accent-deep-b, #8b5cf6) 6%, transparent)' : 'var(--ds-canvas)',
  cursor: 'pointer',
  transition: 'border-color var(--ds-duration-fast) var(--ds-ease), background var(--ds-duration-fast) var(--ds-ease)',
  '&:hover': {
    borderColor: $selected ? 'var(--ds-accent-deep-b, #8b5cf6)' : 'var(--ds-ink-muted)',
  },
}))

const VisIcon = styled(Box, {
  shouldForwardProp: (p) => p !== '$selected',
})<{ $selected?: boolean }>(({ $selected }) => ({
  fontSize: 22,
  color: $selected ? 'var(--ds-accent-deep-b, #8b5cf6)' : 'var(--ds-ink-subtle)',
  display: 'flex',
}))

const VisLabel = styled('div')({ fontSize: 13.5, fontWeight: 660, color: 'var(--ds-ink)' })
const VisSub = styled('div')({ fontSize: 11.5, color: 'var(--ds-ink-subtle)' })

const ICON_MAP: Record<number, React.ReactNode> = {
  3: <LockOutlinedIcon fontSize='inherit' />,
  4: <PeopleOutlinedIcon fontSize='inherit' />,
  5: <GroupsOutlinedIcon fontSize='inherit' />,
}

// ─── Connection picker ────────────────────────────────────────────────────────

const PickerBox = styled(Box)({
  marginTop: 18,
  border: '1px solid var(--ds-border)',
  borderRadius: 'var(--ds-radius-md)',
  overflow: 'hidden',
})

const PickerHead = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '11px 14px',
  borderBottom: '1px solid var(--ds-border)',
  background: 'var(--ds-hover)',
})

const PickerBody = styled(Box)({
  maxHeight: 280,
  overflowY: 'auto',
  padding: '6px 0',
})

const PickerRow = styled(Box, {
  shouldForwardProp: (p) => p !== '$checked',
})<{ $checked?: boolean }>(({ $checked }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '9px 14px',
  cursor: 'pointer',
  background: $checked ? 'color-mix(in srgb, var(--ds-accent-deep-b, #8b5cf6) 6%, transparent)' : 'transparent',
  '&:hover': { background: $checked ? 'color-mix(in srgb, var(--ds-accent-deep-b, #8b5cf6) 9%, transparent)' : 'var(--ds-hover)' },
}))

const PickerName = styled('div')({ fontSize: 13, fontWeight: 590, color: 'var(--ds-ink)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })

const Checkbox = styled('span', {
  shouldForwardProp: (p) => p !== '$checked',
})<{ $checked?: boolean }>(({ $checked }) => ({
  width: 18,
  height: 18,
  borderRadius: 5,
  border: `1.5px solid ${$checked ? 'var(--ds-accent-deep-b, #8b5cf6)' : 'var(--ds-border-strong)'}`,
  background: $checked ? 'var(--ds-accent-deep-b, #8b5cf6)' : 'var(--ds-canvas)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  fontSize: 11,
  color: '#fff',
  transition: 'background var(--ds-duration-fast) var(--ds-ease), border-color var(--ds-duration-fast) var(--ds-ease)',
}))

const CountPill = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  height: 26,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10,
  fontWeight: 600,
  padding: '0 10px',
  borderRadius: 'var(--ds-radius-pill)',
  background: 'var(--ds-accent-deep-b, #8b5cf6)',
  color: '#fff',
})

const PickerHeadTrailing = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexShrink: 0,
})

const EmptyNote = styled('div')({
  padding: '20px 14px',
  fontSize: 12.5,
  color: 'var(--ds-ink-subtle)',
  textAlign: 'center',
})

interface ConnectionsPickerProps {
  selected: string[]
  onChange: (ids: string[]) => void
  invites: string[]
  onAddInvite: (email: string) => void
}

function ConnectionsPicker({ selected, onChange, invites, onAddInvite }: ConnectionsPickerProps) {
  const [myConnections] = useAppStore((state) => [state.user.myConnections], shallow)
  const [search, setSearch] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviting, setInviting] = useState(false)
  const invite = useInviteUser()

  const filtered = useMemo(() => {
    if (!search) return myConnections
    const q = search.toLowerCase()
    return myConnections.filter((c) =>
      `${c.firstName} ${c.lastName} ${c.profession}`.toLowerCase().includes(q)
    )
  }, [myConnections, search])

  const toggle = (userID: string) => {
    onChange(
      selected.includes(userID)
        ? selected.filter((id) => id !== userID)
        : [...selected, userID]
    )
  }

  const handleInvite = async () => {
    if (!inviteEmail || !validateEmail(inviteEmail)) return
    setInviting(true)
    try {
      await invite.mutateAsync({ email: inviteEmail, phone: '' })
      onAddInvite(inviteEmail)
      setInviteEmail('')
      toast.success('Invite sent')
    } catch {
      toast.error('Could not send invite')
    } finally {
      setInviting(false)
    }
  }

  return (
    <>
      <PickerBox>
        <PickerHead>
          <SearchIcon sx={{ fontSize: 15, color: 'var(--ds-ink-subtle)' }} />
          <SearchInput
            icon=''
            placeholder='Filter connections…'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ fontSize: 12.5, flex: 1 }}
          />
          {selected.length > 0 && <CountPill>{selected.length} selected</CountPill>}
        </PickerHead>
        <PickerBody>
          {filtered.length === 0 ? (
            <EmptyNote>{search ? 'No matches' : 'No connections yet'}</EmptyNote>
          ) : (
            filtered.map((conn) => {
              const name = `${conn.firstName} ${conn.lastName}`
              const src = conn.thumbImgURL && isValidAvatarUrl(conn.thumbImgURL) ? conn.thumbImgURL : undefined
              const checked = selected.includes(conn.userID)
              return (
                <PickerRow key={conn.userID} $checked={checked} onClick={() => toggle(conn.userID)}>
                  <Avatar
                    src={src}
                    sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700, background: avatarGradient(name), color: 'var(--ds-accent-ink)', flexShrink: 0 }}
                  >
                    {conn.firstName[0]?.toUpperCase()}
                  </Avatar>
                  <PickerName>{name}</PickerName>
                  <Checkbox $checked={checked}>{checked ? '✓' : ''}</Checkbox>
                </PickerRow>
              )
            })
          )}
        </PickerBody>
      </PickerBox>

      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            height: 36,
            padding: '0 12px',
            borderRadius: 'var(--ds-radius-md)',
            boxShadow: 'inset 0 0 0 1px var(--ds-border-strong)',
            background: 'var(--ds-canvas)',
            gap: 8,
            fontSize: 13,
            color: 'var(--ds-ink-subtle)',
          }}
        >
          <span style={{ fontSize: 13 }}>@</span>
          <Input
            variant='filled'
            placeholder='Invite by email address…'
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
            sx={{ flex: 1, background: 'transparent', boxShadow: 'none', height: 36, '& .MuiInputBase-input': { padding: 0, fontSize: 13 } }}
          />
        </Box>
        <Button
          variant='secondary'
          size='sm'
          disabled={!inviteEmail || !validateEmail(inviteEmail)}
          loading={inviting}
          onClick={handleInvite}
        >
          Invite
        </Button>
      </Box>

      {invites.length > 0 && (
        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {invites.map((e) => (
            <Box
              key={e}
              sx={{
                fontSize: 11,
                fontWeight: 600,
                padding: '3px 10px',
                borderRadius: 'var(--ds-radius-pill)',
                background: 'var(--ds-hover)',
                color: 'var(--ds-ink-subtle)',
              }}
            >
              {e}
            </Box>
          ))}
        </Box>
      )}
    </>
  )
}

// ─── Circles picker ───────────────────────────────────────────────────────────

interface CirclesPickerProps {
  selectedIDs: string[]
  selectedTypes: string[]
  onChange: (ids: string[], types: string[]) => void
}

function CirclesPicker({ selectedIDs, selectedTypes, onChange }: CirclesPickerProps) {
  const [myGroups] = useAppStore((state) => [state.user.myGroups], shallow)
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newType, setNewType] = useState(1)
  const createGroup = useCreateGroup()
  const getMyCircles = useGetMyCircles()

  const filtered = useMemo(() => {
    if (!search) return myGroups
    return myGroups.filter((g) => g.circleName.toLowerCase().includes(search.toLowerCase()))
  }, [myGroups, search])

  const toggle = (circleID: string, circleTypeID: string) => {
    const idx = selectedIDs.indexOf(circleID)
    if (idx < 0) {
      onChange([...selectedIDs, circleID], [...selectedTypes, circleTypeID])
    } else {
      const ids = [...selectedIDs]
      const types = [...selectedTypes]
      ids.splice(idx, 1)
      types.splice(idx, 1)
      onChange(ids, types)
    }
  }

  const handleCreate = async () => {
    if (!newName.trim()) return
    try {
      await createGroup.mutateAsync({ circleName: newName.trim(), circleTypeID: newType })
      await getMyCircles.mutateAsync()
      setNewName('')
      setCreating(false)
      toast.success('Circle created')
    } catch {
      toast.error('Could not create circle')
    }
  }

  return (
    <>
      <PickerBox>
        <PickerHead>
          <SearchIcon sx={{ fontSize: 15, color: 'var(--ds-ink-subtle)' }} />
          <SearchInput
            icon=''
            placeholder='Filter circles…'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ fontSize: 12.5, flex: 1 }}
          />
          <PickerHeadTrailing>
            {selectedIDs.length > 0 && <CountPill>{selectedIDs.length} selected</CountPill>}
            <Box
              component='button'
              onClick={() => setCreating(true)}
              sx={{
                all: 'unset',
                boxSizing: 'border-box',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                height: 26,
                padding: '0 12px',
                borderRadius: 'var(--ds-radius-pill)',
                fontSize: 11.5,
                fontWeight: 640,
                lineHeight: 1,
                background: 'var(--ds-ink)',
                color: 'var(--ds-inverse-ink)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                transition: 'opacity var(--ds-duration-fast) var(--ds-ease)',
                '&:hover': { opacity: 0.85 },
              }}
            >
              <AddIcon sx={{ fontSize: 13 }} /> New
            </Box>
          </PickerHeadTrailing>
        </PickerHead>
        <PickerBody>
          {filtered.length === 0 ? (
            <EmptyNote>{search ? 'No matches' : 'No circles yet — create one above'}</EmptyNote>
          ) : (
            filtered.map((g) => {
              const checked = selectedIDs.includes(g.circleID)
              const style = circleTypeStyle(g.circleTypeID)
              return (
                <PickerRow key={g.circleID} $checked={checked} onClick={() => toggle(g.circleID, g.circleTypeID)}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: 'var(--ds-radius-sm)',
                      background: style.bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      flexShrink: 0,
                      userSelect: 'none',
                    }}
                  >
                    {style.emoji}
                  </Box>
                  <PickerName>{g.circleName}</PickerName>
                  <Checkbox $checked={checked}>{checked ? '✓' : ''}</Checkbox>
                </PickerRow>
              )
            })
          )}
        </PickerBody>
      </PickerBox>

      {creating && (
        <Box
          sx={{
            mt: 2,
            padding: '14px 16px',
            borderRadius: 'var(--ds-radius-md)',
            border: '1px solid var(--ds-border)',
            background: 'var(--ds-canvas)',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 660, color: 'var(--ds-ink)' }}>Create circle</Typography>
            <Box
              component='button'
              onClick={() => setCreating(false)}
              sx={{ all: 'unset', cursor: 'pointer', color: 'var(--ds-ink-subtle)', display: 'flex', '&:hover': { color: 'var(--ds-ink)' } }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 10 }}>
            <Input
              variant='filled'
              placeholder='Circle name…'
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              inputProps={{ maxLength: 20 }}
              sx={{ flex: 1, height: 36, padding: '0 12px', background: 'var(--ds-canvas)', boxShadow: 'inset 0 0 0 1px var(--ds-border-strong)', '& .MuiInputBase-input': { padding: 0, fontSize: 13 }, '&.Mui-focused': { boxShadow: 'inset 0 0 0 1.5px var(--ds-accent-deep-b)' } }}
            />
            <Box
              component='select'
              value={newType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setNewType(Number(e.target.value))}
              sx={{ height: 36, padding: '0 10px', borderRadius: 'var(--ds-radius-md)', border: '1px solid var(--ds-border-strong)', background: 'var(--ds-canvas)', fontSize: 13, color: 'var(--ds-ink)', cursor: 'pointer' }}
            >
              {GroupOptions.map((g) => (
                <option key={g.type} value={g.type}>{g.name}</option>
              ))}
            </Box>
            <Button
              variant='primary'
              size='sm'
              disabled={!newName.trim()}
              loading={createGroup.isLoading}
              onClick={handleCreate}
            >
              Create
            </Button>
          </Box>
        </Box>
      )}
    </>
  )
}

// ─── Main section ─────────────────────────────────────────────────────────────

interface CachedVisibility {
  userID: string
  accessTypeID: number
  selectedConnections: string[]
  selectedCircleIDs: string[]
  selectedCircleTypes: string[]
}

export function PrivacySection() {
  const [ewUser] = useLocalStorage('ewUser', null) as [{ userID: string } | null, unknown]

  // The backend's visibility-load endpoint can be unreliable (returns a generic
  // server error instead of the saved permission). When that happens we fall back
  // to the last selection we successfully saved *from this device*, so a refresh
  // doesn't look like the save was silently lost. This is a client-side safety
  // net only — it's still overwritten by the real server value whenever the load
  // succeeds.
  const [cachedVisibility, setCachedVisibility] = useLocalStorage(
    'privacyVisibilityCache',
    null
  ) as [CachedVisibility | null, (value: CachedVisibility | null) => void]

  const [myPermission, profile] = useAppStore(
    (state) => [state.user.myPermission, state.user.profile],
    shallow
  )

  const [showConnCount, setShowConnCount] = useState(profile?.showConnCount === '1')
  const [accessTypeID, setAccessTypeID] = useState<number>(0)
  const [selectedConnections, setSelectedConnections] = useState<string[]>([])
  const [selectedCircleIDs, setSelectedCircleIDs] = useState<string[]>([])
  const [selectedCircleTypes, setSelectedCircleTypes] = useState<string[]>([])
  const [invites, setInvites] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [permissionsLoadFailed, setPermissionsLoadFailed] = useState(false)
  const hasEditedShowConnCount = useRef(false)

  const getPermissions = useGetPermissionsProfile(ewUser?.userID ?? '')
  const updateVisibility = useUpdateVisibility()
  const updateProfile = useUpdateProfile()
  const getProfileData = useGetBasicInfo(ewUser?.userID ?? '')
  const getMyCircles = useGetMyCircles()

  // Keep in sync with the store in case the profile finishes loading after mount
  // instead of resetting the toggle back to off. Stops once the user has touched
  // the toggle themselves, so an unrelated profile refetch elsewhere in the app
  // (e.g. the nav bar) can't silently snap their in-progress edit back.
  useEffect(() => {
    if (hasEditedShowConnCount.current) return
    setShowConnCount(profile?.showConnCount === '1')
  }, [profile?.showConnCount])

  const handleToggleShowConnCount = (checked: boolean) => {
    hasEditedShowConnCount.current = true
    setShowConnCount(checked)
  }

  function loadPermissions() {
    setPermissionsLoadFailed(false)
    getPermissions.mutateAsync().then((perm) => {
      const selected = perm?.permissionData?.find((p) => p.isSelected === 'true')
      if (selected) {
        const connections = selected.accessList?.connections?.filter(Boolean) ?? []
        const circleIDs = selected.accessList?.cliques?.filter(Boolean) ?? []
        const circleTypes = selected.accessList?.cliqueTypes?.filter(Boolean) ?? []
        setAccessTypeID(selected.accessTypeID)
        setSelectedConnections(connections)
        setSelectedCircleIDs(circleIDs)
        setSelectedCircleTypes(circleTypes)
        // The server confirmed a real value — replace any stale local cache with it.
        if (ewUser?.userID) {
          setCachedVisibility({
            userID: ewUser.userID,
            accessTypeID: selected.accessTypeID,
            selectedConnections: connections,
            selectedCircleIDs: circleIDs,
            selectedCircleTypes: circleTypes,
          })
        }
      } else {
        // No visibility configured yet for this user — default to the safest option
        // (Private) so the picker always shows a selection rather than none at all.
        setAccessTypeID(3)
      }
    }).catch(() => {
      // Permissions failed to load (backend error). Prefer the last selection we
      // know we successfully saved from this device over blindly guessing Private
      // — otherwise every save looks like it "didn't work" the moment the load
      // endpoint has a bad day, even though it actually persisted server-side.
      if (cachedVisibility && cachedVisibility.userID === ewUser?.userID) {
        setAccessTypeID(cachedVisibility.accessTypeID)
        setSelectedConnections(cachedVisibility.selectedConnections)
        setSelectedCircleIDs(cachedVisibility.selectedCircleIDs)
        setSelectedCircleTypes(cachedVisibility.selectedCircleTypes)
      } else {
        // Nothing saved from this device either — default to the safest option
        // (Private) so the picker still shows a selection rather than none at all.
        setAccessTypeID(3)
      }
      setPermissionsLoadFailed(true)
    })
  }

  useEffect(() => {
    if (!ewUser?.userID) return

    loadPermissions()
    getMyCircles.mutateAsync()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ewUser?.userID])

  const handleSave = async () => {
    if (!ewUser?.userID) return
    setSaving(true)
    try {
      if (showConnCount !== (profile?.showConnCount === '1')) {
        await updateProfile.mutateAsync({ showConnCount: showConnCount ? 1 : 0 })
        await getProfileData.mutateAsync({ isUserProfile: false })
        hasEditedShowConnCount.current = false
      }

      if (showConnCount && accessTypeID > 0) {
        await updateVisibility.mutateAsync({
          resourceID: ewUser.userID,
          cliqueTypes: accessTypeID === 5 ? selectedCircleTypes : [],
          cliques: accessTypeID === 5 ? selectedCircleIDs : [],
          connections: accessTypeID === 4 ? selectedConnections : [],
          accessTypeID: String(accessTypeID),
          permissionID: myPermission?.permissionID ?? '',
          resourceType: 4,
        })
        // Remember what we just successfully saved so a refresh can show it even
        // if the (separate, flakier) load endpoint fails to confirm it back.
        setCachedVisibility({
          userID: ewUser.userID,
          accessTypeID,
          selectedConnections: accessTypeID === 4 ? selectedConnections : [],
          selectedCircleIDs: accessTypeID === 5 ? selectedCircleIDs : [],
          selectedCircleTypes: accessTypeID === 5 ? selectedCircleTypes : [],
        })
      }

      fireAlertToast('settings.privacySaved')
    } catch {
      fireAlertToast('settings.privacySaveFailed')
    } finally {
      setSaving(false)
    }
  }

  const visibilityReady = !getPermissions.isLoading

  return (
    <Box>
      <PanelHead>
        <H2>Privacy</H2>
        <Sub>Control who can see your connections and how your profile appears to others.</Sub>
      </PanelHead>

      <FormGroup label='Connection count'>
        <SetRow>
          <RowLft>
            <RowIc sx={{ background: 'color-mix(in srgb, var(--ds-accent-deep-b, #8b5cf6) 12%, transparent)', color: 'var(--ds-accent-deep-b, #8b5cf6)' }}>
              <PeopleOutlinedIcon fontSize='small' />
            </RowIc>
            <Box>
              <RowT>Show connection count</RowT>
              <RowS>Display the number of people in your network on your profile</RowS>
            </Box>
          </RowLft>
          <Toggle
            checked={showConnCount}
            onChange={handleToggleShowConnCount}
            aria-label='Show connection count'
          />
        </SetRow>
      </FormGroup>

      {showConnCount && (
        <FormGroup label='Who can see your connections?'>
          {getPermissions.isLoading ? (
            <Box sx={{ fontSize: 13, color: 'var(--ds-ink-subtle)', py: 1 }}>Loading…</Box>
          ) : (
            <>
              {permissionsLoadFailed && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, mb: 1.5 }}>
                  <Typography sx={{ fontSize: 12.5, color: 'var(--ds-accent-deep-a, #ef4444)' }}>
                    {cachedVisibility && cachedVisibility.userID === ewUser?.userID
                      ? "Couldn't reconfirm with the server — showing what you last saved from this device."
                      : "Couldn't confirm your previously saved setting — defaulted to Private."}
                  </Typography>
                  <Button variant='secondary' size='sm' onClick={loadPermissions}>
                    Retry
                  </Button>
                </Box>
              )}
              <VisibilityGrid>
                {ProfilePrivacyData.map((opt) => {
                  const selected = opt.id === accessTypeID
                  return (
                    <VisCard key={opt.id} $selected={selected} onClick={() => setAccessTypeID(opt.id)}>
                      <VisIcon $selected={selected}>{ICON_MAP[opt.id]}</VisIcon>
                      <VisLabel>{opt.title}</VisLabel>
                      <VisSub>{opt.subTitle}</VisSub>
                    </VisCard>
                  )
                })}
              </VisibilityGrid>

              {visibilityReady && accessTypeID === 4 && (
                <ConnectionsPicker
                  selected={selectedConnections}
                  onChange={setSelectedConnections}
                  invites={invites}
                  onAddInvite={(email) => setInvites((prev) => [...prev, email])}
                />
              )}

              {visibilityReady && accessTypeID === 5 && (
                <CirclesPicker
                  selectedIDs={selectedCircleIDs}
                  selectedTypes={selectedCircleTypes}
                  onChange={(ids, types) => {
                    setSelectedCircleIDs(ids)
                    setSelectedCircleTypes(types)
                  }}
                />
              )}
            </>
          )}
        </FormGroup>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 1, pt: 2, borderTop: '1px solid var(--ds-border)' }}>
        {showConnCount && accessTypeID === 4 && selectedConnections.length > 0 && (
          <Typography sx={{ fontSize: 12.5, color: 'var(--ds-ink-subtle)', alignSelf: 'center' }}>
            {selectedConnections.length} connection{selectedConnections.length !== 1 ? 's' : ''} selected
            {invites.length > 0 ? ` · ${invites.length} invite${invites.length !== 1 ? 's' : ''} sent` : ''}
          </Typography>
        )}
        {showConnCount && accessTypeID === 5 && selectedCircleIDs.length > 0 && (
          <Typography sx={{ fontSize: 12.5, color: 'var(--ds-ink-subtle)', alignSelf: 'center' }}>
            {selectedCircleIDs.length} circle{selectedCircleIDs.length !== 1 ? 's' : ''} selected
          </Typography>
        )}
        <Button variant='primary' size='sm' loading={saving} onClick={handleSave}>
          Save privacy
        </Button>
      </Box>
    </Box>
  )
}
